import { useState, useEffect, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/api';
import { useAuthStore } from '@store/index';
import { toDisplayError } from '../../utils/apiError';
import ChangeEmailDialog from './ChangeEmailDialog';
import { TIMEZONES } from '../../utils/timezones';

interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string | null;
  timezone?: string | null;
  /** REV-683: an address change waiting for confirmation. */
  pending_email?: string | null;
  avatar_url?: string | null;
  role: string;
  locale?: string | null;
}

const detailsSchema = z.object({
  firstName: z.string().trim().min(1, 'profile.validation.firstNameRequired'),
  lastName: z.string().trim().min(1, 'profile.validation.lastNameRequired'),
  phone: z.string().optional(),
  timezone: z.string().optional(),
  avatarUrl: z
    .string()
    .optional()
    .refine((v) => !v || /^https?:\/\/.+/.test(v), 'profile.validation.avatarUrlInvalid'),
});

type DetailsFormData = z.infer<typeof detailsSchema>;

/**
 * REV-491: the profile details form, moved out of the standalone /profile page.
 *
 * User configuration was split across two destinations — /profile held name,
 * avatar, timezone and password while /settings held a single email toggle.
 * That split is what the walkthrough hit as "Settings only has email
 * preferences". Both now live as tabs of /settings; this is one of them.
 *
 * Behaviour is unchanged from the old page: same endpoints, same validation,
 * same auth-store update on save.
 */
export default function ProfileDetailsTab() {
  const { t } = useTranslation();
  const setUser = useAuthStore((s) => s.setUser);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  // REV-683: the address waiting for confirmation, and the change dialog.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeRequested, setChangeRequested] = useState<string | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const detailsForm = useForm<DetailsFormData>({
    resolver: zodResolver(detailsSchema),
    defaultValues: { firstName: '', lastName: '', phone: '', timezone: '', avatarUrl: '' },
  });

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await apiClient.get<{ success: boolean; data: UserProfile }>('/v1/users/me');
      const u = res.data;
      setEmail(u.email);
      setPendingEmail(u.pending_email ?? null);
      detailsForm.reset({
        firstName: u.first_name ?? '',
        lastName: u.last_name ?? '',
        phone: u.phone ?? '',
        // An account always has a zone (the API defaults it to UTC); seed the
        // form with the zone the combobox shows, so display and save agree.
        timezone: u.timezone || 'UTC',
        avatarUrl: u.avatar_url ?? '',
      });
    } catch {
      setLoadError(t('profile.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [detailsForm, t]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const onSaveDetails = async (data: DetailsFormData) => {
    setDetailsError(null);
    setDetailsSaved(false);
    setSavingDetails(true);
    try {
      const res = await apiClient.put<{ success: boolean; data: UserProfile }>('/v1/users/me', {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || null,
        timezone: data.timezone || undefined,
        avatarUrl: data.avatarUrl || null,
      });
      const u = res.data;
      setUser({
        id: u.id,
        email: u.email,
        first_name: u.first_name,
        last_name: u.last_name,
        role: u.role,
        // REV-798: this response is the whole profile; dropping the field here
        // would clear the stored language preference from the auth store.
        locale: u.locale ?? null,
      });
      setDetailsSaved(true);
    } catch (error: unknown) {
      setDetailsError(toDisplayError(error, t('profile.errors.saveFailed')));
    } finally {
      setSavingDetails(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
        <CircularProgress size={24} aria-hidden />
        <Typography>{t('profile.loading')}</Typography>
      </Box>
    );
  }

  return (
    <>
      {loadError && (
        <Alert severity="error" role="alert" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {t('profile.details.title')}
        </Typography>
        {detailsSaved && (
          <Alert severity="success" role="status" sx={{ mb: 2 }}>
            {t('profile.details.saved')}
          </Alert>
        )}
        {detailsError && (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {detailsError}
          </Alert>
        )}
        <Box
          component="form"
          onSubmit={detailsForm.handleSubmit(onSaveDetails)}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {/* REV-683: read-only, not disabled: a disabled field is skipped by
              Tab and its value can be hard to read. The change goes through
              the dialog, which confirms the new address by email. */}
          <TextField
            label={t('profile.details.email')}
            type="email"
            value={email}
            helperText={
              pendingEmail
                ? t('profile.email.pending', { pending: pendingEmail, email })
                : undefined
            }
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { readOnly: true } }}
          />
          {changeRequested && (
            <Alert severity="success" role="status">
              {t('profile.email.requested', { pending: changeRequested, email })}
            </Alert>
          )}
          <Box>
            <Button variant="outlined" onClick={() => setChangeOpen(true)}>
              {t('profile.email.change')}
            </Button>
          </Box>
          <TextField
            {...detailsForm.register('firstName')}
            label={t('profile.details.firstName')}
            autoComplete="given-name"
            error={!!detailsForm.formState.errors.firstName}
            helperText={
              detailsForm.formState.errors.firstName
                ? t(detailsForm.formState.errors.firstName.message as string)
                : undefined
            }
            disabled={savingDetails}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            {...detailsForm.register('lastName')}
            label={t('profile.details.lastName')}
            autoComplete="family-name"
            error={!!detailsForm.formState.errors.lastName}
            helperText={
              detailsForm.formState.errors.lastName
                ? t(detailsForm.formState.errors.lastName.message as string)
                : undefined
            }
            disabled={savingDetails}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            {...detailsForm.register('phone')}
            label={t('profile.details.phone')}
            autoComplete="tel"
            disabled={savingDetails}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {/* REV-684: every IANA zone, searchable, rather than free text the
              API had to take on trust. disableClearable: an account always has
              a zone. */}
          <Controller
            control={detailsForm.control}
            name="timezone"
            render={({ field }) => (
              <Autocomplete
                // A zone saved from another engine may not be in this
                // browser's list (Asia/Kolkata vs Asia/Calcutta): offer it too.
                options={
                  field.value && !TIMEZONES.includes(field.value)
                    ? [field.value, ...TIMEZONES]
                    : TIMEZONES
                }
                value={field.value || 'UTC'}
                onChange={(_e, zone) => field.onChange(zone)}
                disableClearable
                disabled={savingDetails}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={field.ref}
                    label={t('profile.details.timezone')}
                    onBlur={field.onBlur}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
              />
            )}
          />
          <TextField
            {...detailsForm.register('avatarUrl')}
            label={t('profile.details.avatarUrl')}
            type="url"
            error={!!detailsForm.formState.errors.avatarUrl}
            helperText={
              detailsForm.formState.errors.avatarUrl
                ? t(detailsForm.formState.errors.avatarUrl.message as string)
                : undefined
            }
            disabled={savingDetails}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={savingDetails}>
              {savingDetails ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('profile.details.save')
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
      <ChangeEmailDialog
        open={changeOpen}
        currentEmail={email}
        onClose={() => setChangeOpen(false)}
        onRequested={(pending) => {
          setChangeOpen(false);
          setPendingEmail(pending);
          setChangeRequested(pending);
        }}
      />
    </>
  );
}
